import React, { useState, useRef } from 'react';
import { Battery, FilterState } from '../types';
import * as XLSX from 'xlsx';
import { Button, ButtonDivider, Toolbar, ToolbarGroup } from './ButtonGroup';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface BatteryDatabaseTabProps {
  batteries: Battery[];
  onBatteriesChange: (batteries: Battery[]) => void;
  showStatus: (message: string, type?: 'success' | 'warning' | 'error') => void;
}

const BatteryDatabaseTab: React.FC<BatteryDatabaseTabProps> = ({ batteries, onBatteriesChange, showStatus }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  
  // 通用的无边框输入框样式
  const seamlessInputStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: colors.text,
    fontSize: '14px',
    width: 'auto',
    minWidth: '20px',
    padding: '2px',
  };

  // 计算输入框宽度的函数
  const calculateInputWidth = (value: string | number) => {
    const str = String(value);
    // 基础宽度 + 每个字符的宽度（大约10px）+ 更多padding
    const baseWidth = 30;
    const charWidth = 10;
    const padding = 24;
    return Math.max(baseWidth, str.length * charWidth + padding);
  };
  const [filters, setFilters] = useState<FilterState>({
    brand: '',
    capacityRange: '',
    dischargeRange: ''
  });
  const [selectedBatteries, setSelectedBatteries] = useState<Set<string>>(new Set());
  const [filteredBatteries, setFilteredBatteries] = useState<Battery[]>(batteries);
  const [editingCell, setEditingCell] = useState<{ batteryId: string; field: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 应用筛选条件
  React.useEffect(() => {
    let filtered = [...batteries];

    if (filters.brand) {
      filtered = filtered.filter(battery => battery.brand === filters.brand);
    }

    if (filters.capacityRange) {
      const [min, max] = filters.capacityRange.split('-').map(Number);
      if (max) {
        filtered = filtered.filter(battery => battery.capacity >= min && battery.capacity <= max);
      } else {
        filtered = filtered.filter(battery => battery.capacity >= min);
      }
    }

    if (filters.dischargeRange) {
      const [min, max] = filters.dischargeRange.split('-').map(Number);
      if (max) {
        filtered = filtered.filter(battery => battery.discharge >= min && battery.discharge <= max);
      } else {
        filtered = filtered.filter(battery => battery.discharge >= min);
      }
    }

    setFilteredBatteries(filtered);
  }, [batteries, filters]);

  const handleFilterChange = (field: keyof FilterState, value: string | number | undefined) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      capacityRange: '',
      dischargeRange: ''
    });
    showStatus(t('battery.messages.filterCleared'));
  };

  const addBattery = () => {
    const newBattery: Battery = {
      id: Date.now().toString(),
      brand: '',
      model: '',
      cells: 4,
      capacity: 0,
      voltage: 0,
      energy: 0,
      weight: 0,
      discharge: 0,
      size: '',
      price: 0,
      notes: ''
    };
    onBatteriesChange([...batteries, newBattery]);
    showStatus(t('battery.messages.batteryAdded'));
  };

  const deleteSelectedBatteries = () => {
    const remainingBatteries = batteries.filter(battery => !selectedBatteries.has(battery.id!));
    onBatteriesChange(remainingBatteries);
    setSelectedBatteries(new Set());
    showStatus(t('battery.messages.batteriesDeleted', { count: selectedBatteries.size }));
  };

  const selectAllBatteries = (checked: boolean) => {
    if (checked) {
      setSelectedBatteries(new Set([...filteredBatteries.map(battery => battery.id!)]));
    } else {
      setSelectedBatteries(new Set());
    }
  };

  const selectBattery = (batteryId: string, checked: boolean) => {
    const newSelected = new Set(selectedBatteries);
    if (checked) {
      newSelected.add(batteryId);
    } else {
      newSelected.delete(batteryId);
    }
    setSelectedBatteries(newSelected);
  };

  const updateBattery = (batteryId: string, field: keyof Battery, value: string | number) => {
    const updatedBatteries = batteries.map(battery => {
      if (battery.id === batteryId) {
        return { ...battery, [field]: value };
      }
      return battery;
    });
    onBatteriesChange(updatedBatteries);
  };

  // 切换电池的分析状态
  const toggleAnalysisStatus = (batteryId: string) => {
    const updatedBatteries = batteries.map(battery => {
      if (battery.id === batteryId) {
        return { ...battery, includeInAnalysis: !battery.includeInAnalysis };
      }
      return battery;
    });
    onBatteriesChange(updatedBatteries);
    
    const battery = batteries.find(b => b.id === batteryId);
    const newStatus = !battery?.includeInAnalysis;
    const name = `${battery?.brand} ${battery?.model}`;
    showStatus(t(newStatus ? 'battery.messages.addedToAnalysis' : 'battery.messages.removedFromAnalysis', { name }));
  };

  // 批量添加到分析
  const addSelectedToAnalysis = () => {
    const updatedBatteries = batteries.map(battery => {
      if (selectedBatteries.has(battery.id!)) {
        return { ...battery, includeInAnalysis: true };
      }
      return battery;
    });
    onBatteriesChange(updatedBatteries);
    showStatus(t('battery.messages.batchAddedToAnalysis', { count: selectedBatteries.size }));
  };

  // 批量从分析中移除
  const removeSelectedFromAnalysis = () => {
    const updatedBatteries = batteries.map(battery => {
      if (selectedBatteries.has(battery.id!)) {
        return { ...battery, includeInAnalysis: false };
      }
      return battery;
    });
    onBatteriesChange(updatedBatteries);
    showStatus(t('battery.messages.batchRemovedFromAnalysis', { count: selectedBatteries.size }));
  };

  // 统计参与分析的电池数量
  const analysisBatteriesCount = batteries.filter(battery => battery.includeInAnalysis).length;

  const exportToExcel = () => {
    const headers = [
      t('battery.headers.brand'),
      t('battery.headers.model'),
      t('battery.headers.cells'),
      t('battery.headers.capacity'),
      t('battery.headers.voltage'),
      t('battery.headers.energy'),
      t('battery.headers.weight'),
      t('battery.headers.discharge'),
      t('battery.headers.size'),
      t('battery.headers.price'),
      t('battery.headers.notes')
    ];
    const data = [headers];

    batteries.forEach(battery => {
      data.push([
        battery.brand,
        battery.model,
        battery.cells.toString(),
        battery.capacity.toString(),
        battery.voltage.toString(),
        battery.energy.toString(),
        battery.weight.toString(),
        battery.discharge.toString(),
        battery.size,
        battery.price.toString(),
        battery.notes
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    ws['!cols'] = [
      { wch: 12 }, { wch: 15 }, { wch: 8 }, { wch: 12 }, 
      { wch: 12 }, { wch: 10 }, { wch: 8 }, { wch: 12 }, 
      { wch: 15 }, { wch: 8 }, { wch: 20 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, t('battery.sheetName'));
    XLSX.writeFile(wb, `${t('battery.exportFileName')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    showStatus(t('battery.messages.exported', { count: batteries.length }));
  };

  const importFromExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        const expectedHeaders = [
          t('battery.headers.brand'),
          t('battery.headers.model'),
          t('battery.headers.cells'),
          t('battery.headers.capacity'),
          t('battery.headers.voltage'),
          t('battery.headers.energy'),
          t('battery.headers.weight'),
          t('battery.headers.discharge'),
          t('battery.headers.size'),
          t('battery.headers.price'),
          t('battery.headers.notes')
        ];
        const headers = jsonData[0];

        if (!validateHeaders(headers, expectedHeaders)) {
          throw new Error(t('battery.messages.incorrectFormat'));
        }

        const newBatteries: Battery[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row[0] && row[1]) {
            newBatteries.push({
              id: (Date.now() + i).toString(),
              brand: String(row[0] || ''),
              model: String(row[1] || ''),
              cells: parseInt(String(row[2])) || 4,
              capacity: parseFloat(String(row[3])) || 0,
              voltage: parseFloat(String(row[4])) || 0,
              energy: parseFloat(String(row[5])) || 0,
              weight: parseFloat(String(row[6])) || 0,
              discharge: parseFloat(String(row[7])) || 0,
              size: String(row[8] || ''),
              price: parseFloat(String(row[9])) || 0,
              notes: String(row[10] || '')
            });
          }
        }

        if (newBatteries.length > 0) {
          onBatteriesChange(newBatteries);
          showStatus(t('battery.messages.imported', { count: newBatteries.length }));
        } else {
          throw new Error(t('battery.messages.noValidData'));
        }
      } catch (error) {
        showStatus(t('battery.messages.importError', { error: (error as Error).message }), 'error');
      }
    };
    reader.readAsArrayBuffer(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateHeaders = (actualHeaders: any[], expectedHeaders: string[]): boolean => {
    if (!actualHeaders || actualHeaders.length < expectedHeaders.length) {
      return false;
    }
    
    for (let i = 0; i < expectedHeaders.length; i++) {
      if (actualHeaders[i] !== expectedHeaders[i]) {
        return false;
      }
    }
    return true;
  };

  const loadDefaultBatteries = () => {
    import('../data/defaultData').then(({ defaultBatteries }) => {
      onBatteriesChange(defaultBatteries);
      showStatus(t('battery.messages.resetToDefault'));
    });
  };

  const getBrandOptions = () => {
    const brands = [...new Set(batteries.map(battery => battery.brand))].filter(Boolean);
    return brands;
  };

  // 渲染可编辑单元格的通用函数
  const renderEditableCell = (battery: Battery, field: keyof Battery, type: string = 'text', step?: string) => {
    const isEditing = editingCell?.batteryId === battery.id && editingCell?.field === field;
    const value = battery[field] as string | number;
    
    return (
      <td 
        className="px-4 py-3 cursor-pointer"
        style={{ 
          color: colors.text,
          fontSize: '14px',
          fontWeight: '400',
        }}
        onClick={() => setEditingCell({ batteryId: battery.id!, field })}
      >
        {isEditing ? (
          <input
            type={type}
            step={step}
            value={value}
            onChange={(e) => {
              const newValue = type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value;
              updateBattery(battery.id!, field, newValue);
            }}
            onBlur={() => setEditingCell(null)}
            onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
            className="px-2 py-1"
            style={{
              ...seamlessInputStyle,
              width: `${calculateInputWidth(value)}px`,
            }}
            aria-label={`${t('common.edit')} ${field}`}
            autoFocus
          />
        ) : (
          <span>{value}</span>
        )}
      </td>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 
          className="text-xl font-semibold mb-6"
          style={{ 
            color: colors.text,
            fontSize: '20px',
            fontWeight: '600',
            letterSpacing: '-0.025em'
          }}
        >
          {t('battery.title')}
        </h2>

        {/* 筛选栏 */}
        <div 
          className="rounded-lg p-4 mb-6"
          style={{ 
            backgroundColor: colors.surface,
            border: `1px solid ${colors.borderLight}`
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('battery.brandFilter')}
              </label>
              <select
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                aria-label={t('battery.brandFilter')}
              >
                <option value="">{t('battery.filter.all')}</option>
                {getBrandOptions().map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('battery.capacityRangeFilter')}
              </label>
              <select
                value={filters.capacityRange || ''}
                onChange={(e) => handleFilterChange('capacityRange', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                aria-label={t('battery.capacityRangeFilter')}
              >
                <option value="">{t('battery.filter.all')}</option>
                <option value="3000-5000">3000-5000</option>
                <option value="5000-8000">5000-8000</option>
                <option value="8000-99999">8000+</option>
              </select>
            </div>

            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('battery.dischargeRateFilter')}
              </label>
              <select
                value={filters.dischargeRange || ''}
                onChange={(e) => handleFilterChange('dischargeRange', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                aria-label={t('battery.dischargeRateFilter')}
              >
                <option value="">{t('battery.filter.all')}</option>
                <option value="0-25">0-25</option>
                <option value="25-50">25-50</option>
                <option value="50-999">50+</option>
              </select>
            </div>
          </div>
        </div>

        {/* 工具栏 */}
        <Toolbar>
          <ToolbarGroup title={t('battery.dataManagement')}>
            <Button onClick={addBattery} variant="primary">
              {t('battery.addBattery')}
            </Button>
            <Button
              onClick={deleteSelectedBatteries}
              disabled={selectedBatteries.size === 0}
              variant="danger"
            >
              {t('battery.deleteSelected')}
            </Button>
            <Button onClick={loadDefaultBatteries} variant="secondary">
              {t('battery.resetToDefault')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('battery.filterTitle')}>
            <Button onClick={clearFilters} variant="secondary">
              {t('battery.clearFilters')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('battery.importExport')}>
            <Button onClick={exportToExcel} variant="success">
              {t('battery.exportBatteryData')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={importFromExcel}
              className="hidden"
              aria-label={t('battery.importExcelFile')}
            />
            <Button onClick={() => fileInputRef.current?.click()} variant="secondary">
              {t('battery.importFromExcel')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('battery.analysisManagement')}>
            <Button
              onClick={addSelectedToAnalysis}
              disabled={selectedBatteries.size === 0}
              variant="success"
            >
              {t('battery.addToAnalysis')} ({selectedBatteries.size})
            </Button>
            <Button
              onClick={removeSelectedFromAnalysis}
              disabled={selectedBatteries.size === 0}
              variant="warning"
            >
              {t('battery.removeFromAnalysis')} ({selectedBatteries.size})
            </Button>
          </ToolbarGroup>
        </Toolbar>

        {/* 数据表格 */}
        <div 
          className="rounded-lg overflow-hidden"
          style={{ 
            backgroundColor: colors.background,
            border: 'none'
          }}
        >
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead 
                className="sticky top-0 z-10"
                style={{ 
                  backgroundColor: colors.background,
                  borderBottom: `1px solid ${colors.border}`
                }}
              >
                <tr>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={filteredBatteries.length > 0 && selectedBatteries.size === filteredBatteries.length}
                      onChange={(e) => selectAllBatteries(e.target.checked)}
                      aria-label={t('battery.selectDeselectAll')}
                    />
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.brand')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.model')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.cells')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.headers.capacity')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.headers.voltage')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.headers.energy')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.headers.weight')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.headers.discharge')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.headers.size')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.headers.price')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.headers.notes')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('battery.analysisStatus')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBatteries.map((battery) => (
                  <tr 
                    key={battery.id} 
                    className="transition-colors duration-200"
                    style={{
                      backgroundColor: battery.includeInAnalysis 
                        ? (colors.success + '10') 
                        : 'transparent',
                      borderBottom: `1px solid ${colors.borderLight}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.surface;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = battery.includeInAnalysis 
                        ? (colors.success + '10') 
                        : 'transparent';
                    }}
                  >
                    <td 
                      className="px-4 py-3"
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBatteries.has(battery.id!)}
                        onChange={(e) => selectBattery(battery.id!, e.target.checked)}
                        aria-label={`${t('battery.selectBattery')} ${battery.brand} ${battery.model}`}
                      />
                    </td>
                    {renderEditableCell(battery, 'brand')}
                    {renderEditableCell(battery, 'model')}
                    {renderEditableCell(battery, 'cells', 'number')}
                    {renderEditableCell(battery, 'capacity', 'number')}
                    {renderEditableCell(battery, 'voltage', 'number', '0.1')}
                    {renderEditableCell(battery, 'energy', 'number', '0.1')}
                    {renderEditableCell(battery, 'weight', 'number')}
                    {renderEditableCell(battery, 'discharge', 'number')}
                    {renderEditableCell(battery, 'size')}
                    {renderEditableCell(battery, 'price', 'number')}
                    {renderEditableCell(battery, 'notes')}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleAnalysisStatus(battery.id!)}
                        className="px-3 py-1 text-xs font-medium rounded-full transition-colors"
                        style={{
                          backgroundColor: battery.includeInAnalysis 
                            ? (colors.success + '20') 
                            : (colors.textMuted + '20'),
                          color: battery.includeInAnalysis 
                            ? colors.success 
                            : colors.textMuted,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = battery.includeInAnalysis 
                            ? (colors.success + '30') 
                            : (colors.textMuted + '30');
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = battery.includeInAnalysis 
                            ? (colors.success + '20') 
                            : (colors.textMuted + '20');
                        }}
                      >
                        {battery.includeInAnalysis ? '参与' : '不参与'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div 
          className="mt-6 text-sm flex items-center gap-4"
          style={{ 
            color: colors.textSecondary,
            fontSize: '13px',
            fontWeight: '400'
          }}
        >
          <span>
            {t('battery.filterResults', { 
              showing: filteredBatteries.length, 
              total: batteries.length 
            })}
          </span>
          {selectedBatteries.size > 0 && (
            <span>
              {t('battery.selected')} <span style={{ color: colors.text, fontWeight: '500' }}>{selectedBatteries.size}</span> {t('battery.count')}
            </span>
          )}
          <span>
            {t('battery.inAnalysis')}：<span style={{ color: colors.success, fontWeight: '500' }}>{analysisBatteriesCount}</span> {t('battery.count')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BatteryDatabaseTab; 